import { inject, Injectable } from '@angular/core';
import {filter, first, map, switchMap, tap} from 'rxjs/operators';
import { UserBonusesService } from '../../../services/user/user-bonuses.service';
import { UserService } from '../../../services/user/user.service';
import { InstallAppService } from '../../../services/install-app.service';
import { BlogService } from '../../../services/blog.service';
import { BehaviorSubject } from 'rxjs';
import { UserGroupsService } from '../../../services/user/user-groups.service';
import { getRandomNumber } from '../../../helpers/utils';
import {CookieService, PlatformService} from 'ngx-unificator/services';
import { TranslationService } from '../../../services/translation/translation.service';
import { CustomNotificationChanelEnum } from '../../../enums/notifications-enum';
import {
  CustomBlogNotifyType,
  CustomNotifyReloadBonusType,
  CustomNotifyWelcomeBonusType,
  NotificationType,
} from '../../../types/notifications-type';
import {BrandConfigService} from "../../../services/brand-config/brand-config.service";

export const RELOAD_BONUSES_TIME_TO_SHOW = {
  TUESDAY_BONUS: {
    allowed_always: false,
    allowed_days: [2],
    title: 't.tuesday-bonus-title',
    description: 't.tuesday-bonus-description',
    prize: 't.tuesday-bonus-prize',
    secondPrize: 't.tuesday-bonus-second-prize',
    image: '/assets/img/bonuses/tuesday-bonus.png',
    active: false,
    bonusCode: 'TU60',
  },
  TOP_SLOTS: {
    allowed_days: null,
    allowed_always: true,
    title: 't.extra-1-fiat-title',
    description: 't.extra-1-fiat-desc',
    prize: 't.top-slots-prize',
    secondPrize: 't.top-slots-second-prize',
    image: '/assets/img/bonuses/fiat/extra-1.png',
    active: false,
    bonusCode: 'TOP40',
  },
  WEEKEND_RELOAD: {
    allowed_always: false,
    allowed_days: [0, 6],
    title: 't.daily-fiat-title',
    description: 't.daily-fiat-desc',
    prize: 't.weekend-reload-prize',
    secondPrize: 't.weekend-reload-second-prize',
    image: '/assets/img/bonuses/crypto/daily.png',
    active: false,
    bonusCode: 'WEEKEND',
  },
};

@Injectable({
  providedIn: 'root',
})
export class CustomNotificationCenterService {

  private _bonuses = inject(UserBonusesService);
  private _user = inject(UserService);
  private _installApp = inject(InstallAppService);
  private _blog = inject(BlogService);
  private _groups = inject(UserGroupsService);
  private _cookie = inject(CookieService);
  private _translate = inject(TranslationService);
  private _brandConfig = inject(BrandConfigService);
  private _platform = inject(PlatformService);

  /**
   * Use for install app custom chanel
   * @private
   */
  private _installAppChannel$: BehaviorSubject<NotificationType> = new BehaviorSubject<NotificationType>(null);

  /**
   * Use for vip custom chanel
   * @private
   */
  private _vipChannel$: BehaviorSubject<NotificationType> = new BehaviorSubject<NotificationType>(null);

  /**
   * Use for welcome bonuses custom chanel
   * @private
   */
  private _welcomeBonusesChannel$: BehaviorSubject<NotificationType> = new BehaviorSubject<NotificationType>(null);

  /**
   * Use for reload bonuses custom chanel
   * @private
   */
  private _reloadBonusesChannel$: BehaviorSubject<NotificationType[]> = new BehaviorSubject<NotificationType[]>([]);

  /**
   * Use for blog custom chanel
   * @private
   */
  private _blogCustomNotificationChannel$: BehaviorSubject<NotificationType[]> = new BehaviorSubject<NotificationType[]>([]);

  public notificationSubjectList$ = [
    this._installAppChannel$, this._vipChannel$, this._blogCustomNotificationChannel$,
    this._welcomeBonusesChannel$, this._reloadBonusesChannel$,
  ];

  public initCustomNotify() {
    if (this._platform.isBrowser) {
      this._brandConfig.brandConfigResolved$.pipe(
        filter((resp) => !!resp),
        first(),
        tap(() => {
          this._initWelcomeCustomForNotificationForBonuses();
          this._initReloadCustomForNotificationForBonuses();
          this._initInstallForNotification();
          this._initBlogCustomNotificationMessage();
        })
      ).subscribe()
    }
  }

  private _initWelcomeCustomForNotificationForBonuses() {
    this._bonuses.welcomeBonuses$.pipe(
      filter(() => this._user.auth),
    ).subscribe((bonuses) => {
      const activeBonus = bonuses.find(item => item.active);
      this._resolveWelcomeBonusesChannel(activeBonus);
    });
  }

  private _initReloadCustomForNotificationForBonuses() {
    this._user.auth$.pipe(
      filter(auth => !!auth),
      tap(() => {
        const activeReloadBonuses = this.getActiveReloadBonusNotification();
        this._resolveReloadBonusesChannel(activeReloadBonuses);
      }),
    ).subscribe();
  }

  /**
   * Init install notification for notification center
   * @private
   */
  private _initInstallForNotification() {
    if (this._installApp.showInstallAppNotificationMessage) {
      this._resolveInstallAppChannel();
      this._installApp.showInstallAppNotificationMessage = false;
    }
  }

  private _initBlogCustomNotificationMessage() {
    this._user.auth$.pipe(
      filter(auth => !!auth),
      switchMap(() => this._blog.list({
        type: 8,
      })),
      filter(data => !!data),
      map(data => data?.filter(article => !this._cookie.check(article.slug))),
      tap(list => this._resolveBlogNotification(list)),
    ).subscribe();
  }

  private _resolveBlogNotification(list: CustomBlogNotifyType[]) {
    const blogs: NotificationType[] = list.map((item: CustomBlogNotifyType) => {
      return {
        uid: getRandomNumber(111, 111111111111111),
        notify_channel: CustomNotificationChanelEnum.BLOG_NOTIFICATIONS,
        link: item.Link,
        title: item.Title,
        description: item.Description,
        img: item.Image,
        slug: item.slug,
        IsPinned: Boolean(item.IsPinned),
      };
    });
    this._blogCustomNotificationChannel$.next(blogs);
  }

  private _resolveWelcomeBonusesChannel(activeBonus: CustomNotifyWelcomeBonusType) {
    if (!this._groups.isExistGroup('ID407') &&
      !this._cookie.check(`welcome-bonus-${this._user.stats.deposits_count}`)) {
      this._welcomeBonusesChannel$.next({
        uid: getRandomNumber(111, 111111111111111),
        notify_channel: CustomNotificationChanelEnum.WELCOME_BONUSES,
        link: '/deposit',
        title: activeBonus?.Title,
        description: activeBonus?.DescriptionStarterBonus,
        secondDescription: activeBonus?.SecondDescriptionStarterBonus,
        img: activeBonus?.Image,
      });
    }
  }

  private _resolveReloadBonusesChannel(activeBonuses: CustomNotifyReloadBonusType[]) {
    if (!this._cookie.check('reload-bonus')) {
      const current = this._reloadBonusesChannel$.getValue();

      const bonuses: NotificationType[] = activeBonuses.map(activeBonus => ({
        uid: getRandomNumber(111, 111111111111111),
        notify_channel: CustomNotificationChanelEnum.RELOAD_BONUSES,
        link: ['account', 'deposit'],
        title: this._translate.translate(activeBonus.title),
        description: this._translate.translate(activeBonus.description),
        bonusCode: activeBonus?.bonusCode,
        img: activeBonus?.image,
      }));

      this._reloadBonusesChannel$.next([...current, ...bonuses]);
    }
  }

  /**
   * Resolve fields for install app message
   * @private
   */
  private _resolveInstallAppChannel() {
    this._installAppChannel$.next({
      uid: getRandomNumber(111, 111111111111111),
      notify_channel: CustomNotificationChanelEnum.INSTALL_APP,
      link: '/mobile-app',
      title: this._translate.translate('t.all-games-bonuses-app'),
      description: this._translate.translate('link.install-app'),
      img: '/assets/img/mobile-app/icon.png',
    });
  };

  public resolveVipChannel() {
    this._vipChannel$.next({
      uid: getRandomNumber(111, 111111111111111),
      notify_channel: CustomNotificationChanelEnum.VIP,
      link: '/vip-program',
      title: this._translate.translate('t.vip-notification-title'),
      description: this._translate.translate('t.vip-notification-desc'),
      img: '/assets/svg/notify/tournaments.svg',
    });
  }

  public getActiveReloadBonusNotification(date = new Date()): CustomNotifyReloadBonusType[] {
    if (this._groups.isExistGroup('ID407')) {
      const day = date.getUTCDay();

      return Object.entries(RELOAD_BONUSES_TIME_TO_SHOW)
        .filter(([_, bonus]) =>
          bonus.allowed_always || bonus.allowed_days.includes(day))
        .map(([_, { title, description, image, bonusCode, prize, secondPrize, active }]) => ({
          title,
          description,
          image,
          bonusCode,
          prize,
          secondPrize,
          active,
        }));
    } else {
      return [];
    }
  }
}
